import { type FormEvent, useState } from 'react'
import { Link } from 'react-router'
import type { ReviewFlag } from '../../api/types'
import { canFlagIssue, type IssueFilter, issuesShown, issueTarget, type OverviewIssue, queueEmptyText } from '../../lib/overviewStatus'
import { formatDateTime } from '../../lib/time'
import { Dialog } from '../AnalysisUi'
import '../../pages/analysis.css'
import { ArrowIcon, FlagIcon } from './icons'
import { ISSUE_CATEGORIES } from './StatusCards'

/** What the popup is asking, and of what. It holds the issue's names, since the
 *  issue can clear from the queue while someone is still writing. */
type NoteForm = { id: string; kind: 'flag' | 'edit' | 'check'; note: string; title: string; sensor?: string }

/** `showModal` moves focus itself, to the attribute if there is one and otherwise the
 *  first button (the close button). React's autoFocus sets no attribute and runs before
 *  the dialog opens, so it loses; the attribute holds however often the dialog opens. */
const askFirst = (element: HTMLTextAreaElement | null) => { element?.setAttribute('autofocus', '') }

/** How long a flag holds, as the server decides it (design section 7.1). */
function noteHelp({ id, kind }: Pick<NoteForm, 'id' | 'kind'>): string {
  if (kind === 'check') return 'Say what was found. Everyone who sees this issue reads it.'
  if (kind === 'edit') return 'Editing the note does not extend the flag.'
  return id.startsWith('late-') ? 'Flag this for 7 days, then you are asked again. New problems still show.'
    : 'The flag holds until this incident ends. New problems still show.'
}

/** The Needs Attention queue: every issue inside the project filter that
 *  the search leaves, narrowed by the card a reader picked. A flagged issue
 *  is a known one: it leaves All issues for the Flagged view, with why, and
 *  comes back when its incident ends (design section 7.1). Flags and checks
 *  are shared with everyone who sees the issue; a flag never acknowledges or
 *  clears an alarm. Monitoring delayed has no flag button: nobody should be
 *  able to hide that the checks have stopped. */
export function AttentionQueue({ issues, filter, onFilter, flags, details, onFlag, onEditNote, onCheck, onUnflag, flagError, loading, unavailable, narrowed, canManageRules }: {
  /** Inside the project filter and the search, before the card filter. */
  issues: OverviewIssue[]
  filter: IssueFilter
  onFilter: (filter: IssueFilter) => void
  /** The flags that still hold (activeIssueFlags). */
  flags: Set<string>
  /** Those flags in full: who, why, and what a check found. */
  details: Map<string, ReviewFlag>
  /** Each answers whether it saved, so the form closes only when it did. */
  onFlag: (id: string, note: string) => Promise<boolean>
  onEditNote: (id: string, note: string) => Promise<boolean>
  onCheck: (id: string, note: string) => Promise<boolean>
  onUnflag: (id: string) => void
  flagError: string
  loading: boolean
  /** The station list failed to load, so its issues are unknown. */
  unavailable: boolean
  /** A search or project filter is narrowing the queue. */
  narrowed: boolean
  canManageRules: boolean
}) {
  const [form, setForm] = useState<NoteForm | null>(null)
  const [busy, setBusy] = useState(false)
  const visible = issuesShown(issues, filter, flags)
  const flaggedCount = issues.filter(item => flags.has(item.id)).length
  // A card's view still lists what is known, after what is new, and says so.
  const knownHere = filter === 'all' || filter === 'flagged' ? 0 : visible.filter(item => flags.has(item.id)).length
  const chosen = filter === 'flagged' ? 'Flagged' : ISSUE_CATEGORIES.find(item => item.id === filter)?.title
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!form || busy) return
    setBusy(true)
    try {
      const save = form.kind === 'flag' ? onFlag : form.kind === 'edit' ? onEditNote : onCheck
      if (await save(form.id, form.note)) setForm(null)
    } finally { setBusy(false) }
  }
  return <section className="overview-panel ov-review" aria-labelledby="overview-attention-title">
    <div className="ov-review-heading">
      <h2 id="overview-attention-title">Needs Attention <span className="overview-inline-count">{loading ? '…' : issues.length - flaggedCount}</span></h2>
      <div className="ov-review-filters">
        <button type="button" aria-pressed={filter === 'all'} onClick={() => onFilter('all')}>All issues</button>
        <button type="button" aria-pressed={filter === 'flagged'} onClick={() => onFilter('flagged')}><FlagIcon />Flagged <span>{flaggedCount}</span></button>
        {filter !== 'all' && filter !== 'flagged' && <button className="ov-active-filter" type="button" onClick={() => onFilter('all')}
          aria-label={`Clear the ${chosen} filter`}>{chosen}<span aria-hidden="true">×</span></button>}
      </div>
    </div>
    {flagError && <p className="notice notice-warn ov-pad" role="status">{flagError}</p>}
    {knownHere > 0 && <p className="muted ov-pad ov-known-count" role="status">{knownHere} known {knownHere === 1 ? 'issue' : 'issues'}, listed last.</p>}
    <div className="overview-table-scroll"><table className="overview-table ov-issue-table"><thead><tr>
      <th scope="col">Status</th><th scope="col">Station / Sensor</th><th scope="col">Issue</th><th scope="col">Time <span>UTC+4</span></th>
      <th scope="col"><span className="sr-only">Review</span></th>
    </tr></thead><tbody>{visible.map(item => {
      // The alarm evaluation has no page of its own: plain text, never a
      // link that would drop the filters the address carries.
      const target = issueTarget(item, canManageRules)
      const flag = flags.has(item.id) ? details.get(item.id) : undefined
      const name = `${item.title}${item.sensor ? ' ' + item.sensor : ''}`
      const ask = (kind: NoteForm['kind'], note = '') => setForm({ id: item.id, kind, note, title: item.title, sensor: item.sensor })
      return <tr key={item.id} className={flag ? 'ov-flagged-row' : undefined}>
          <td><span className={`pill status-${item.tone}`}>{item.label}</span></td>
          <th scope="row">{item.to ? <Link to={item.to}>{item.title}</Link> : <strong className="ov-issue-title">{item.title}</strong>}
            <span>{item.sensor || item.projects.join(', ') || item.station}</span></th>
          <td>{target ? <Link className="ov-issue-detail" to={target}>{item.detail}<ArrowIcon /></Link>
            : <span className="ov-issue-detail">{item.detail}</span>}
            {flag && <p className="known-issue-note"><strong>Known issue:</strong> {flag.note || 'No reason given.'}<br />
              <small>Flagged by {flag.by}, {formatDateTime(flag.flagged_at)}</small></p>}
            {flag?.checked_at && <p className="known-issue-note known-issue-checked">
              <strong>Checked by {flag.checked_by ?? 'someone'}, {formatDateTime(flag.checked_at)}:</strong> {flag.checked_note}</p>}</td>
          <td className="mono"><span>{item.lastAt ? formatDateTime(item.lastAt) : 'Unavailable'}</span>
            <small className="overview-table-sub">{item.category === 'alarms' ? 'Event opened' : 'Latest reading'}</small></td>
          <td><div className="ov-review-actions">{flag ? <>
            <button type="button" className="ov-text-button" onClick={() => ask('check')}>{flag.checked_at ? 'Check again' : 'Mark checked'}</button>
            <button type="button" className="ov-text-button" onClick={() => ask('edit', flag.note)}>Edit note</button>
            <button type="button" className="ov-flag-button" aria-pressed aria-label={`Restore ${name} ${item.label} to attention`}
              title={`Known issue noted by ${flag.by}. Restore it to attention`} onClick={() => onUnflag(item.id)}><FlagIcon /></button>
          </> : canFlagIssue(item) && <button type="button" className="ov-flag-button" aria-pressed={false}
            aria-label={`Flag ${name} ${item.label} as a known issue`}
            title="Record a known issue and move it to Flagged; everyone sees it" onClick={() => ask('flag')}><FlagIcon /></button>}</div></td>
        </tr>
    })}</tbody></table></div>
    {form && <Dialog title={form.kind === 'flag' ? 'Flag a known issue' : form.kind === 'edit' ? 'Edit the known issue' : 'Mark checked'} className="known-issue-dialog" onClose={() => { if (!busy) setForm(null) }}>
      <form className="wb-form" onSubmit={event => void submit(event)}>
        <div className="known-issue-context"><span className="known-issue-mark" aria-hidden="true"><FlagIcon /></span>
          <div><small>Station / sensor</small><strong>{form.title}</strong>{form.sensor && <span>{form.sensor}</span>}</div></div>
        <p>{noteHelp(form)}</p>
        <label className="analysis-field">{form.kind === 'check' ? 'What was found' : 'Reason'}
          <textarea ref={askFirst} rows={3} maxLength={2000} value={form.note} disabled={busy}
            placeholder={form.kind === 'check' ? 'e.g. Cable replaced, readings back' : 'e.g. Damaged sensor, replacement scheduled'}
            onChange={event => setForm({ ...form, note: event.target.value })} /></label>
        {flagError && <p className="error" role="alert">{flagError}</p>}
        <div className="analysis-dialog-actions">
          {form.kind === 'edit' && <button type="button" className="analysis-button" disabled={busy} onClick={() => { onUnflag(form.id); setForm(null) }}>Restore to attention</button>}
          <button type="button" className="analysis-button" disabled={busy} onClick={() => setForm(null)}>Cancel</button>
          <button type="submit" className="analysis-button primary" disabled={busy || !form.note.trim()}>{busy ? 'Saving…' : form.kind === 'check' ? 'Mark checked' : 'Save known issue'}</button>
        </div>
      </form>
    </Dialog>}
    {loading ? <p className="ov-queue-empty" role="status">Loading monitoring status…</p>
      : !visible.length && <div className="ov-queue-empty" role="status">
        <strong>{queueEmptyText({ unavailable, filter, scoped: issues.length, narrowed })}</strong>
        {filter !== 'all' && <button type="button" onClick={() => onFilter('all')}>Show all issues</button>}
      </div>}
    <footer className="ov-review-foot"><span>{visible.length} {visible.length === 1 ? 'issue' : 'issues'} shown{filter === 'all' && flaggedCount ? `, ${flaggedCount} flagged` : ''}</span>
      <span>A flagged issue is a known one and moves to Flagged until it ends. Flags and checks are shared with everyone who sees the issue. Alarms stay active.</span></footer>
  </section>
}
